const m = require('minthril');
const { component } = require('../../component');
const { Checkbox } = require('../../components/checkbox');
const { RouteSearch } = require('../../components/route-timeline');
const { SiteNavigation } = require('../../components/site-navigation');
const { RendererSelect } = require('../../components/renderer-select');
const { RendererHealth } = require('../../components/renderer-health');

const RouteSettingsPage = component((vnode, emit) => {
  let cacheInfo = null;
  let loadedPath = null;
  let loading = false;
  let saving = false;
  let includeParameter = '';
  let ignoreParameter = '';
  let loadError = false;
  let rendererError = null;

  async function load(path, onLoad) {
    if (!onLoad || loading || loadedPath === path) {
      return;
    }

    loading = true;
    loadError = false;
    try {
      cacheInfo = await onLoad(path);
      loadedPath = path;
    } catch (error) {
      cacheInfo = null;
      loadError = true;
      loadedPath = path;
      console.error(`Unable to load route settings for ${path}.`, error);
    } finally {
      loading = false;
      emit('redraw');
    }
  }

  async function updateParameter(path, value, action, onUpdate) {
    const name = String(value || '').trim();
    if (!name || saving) {
      return;
    }

    saving = true;
    emit('redraw');
    try {
      cacheInfo = { ...cacheInfo, ...await onUpdate(path, name, action) };
      if (action === 'include') includeParameter = '';
      if (action === 'omit') ignoreParameter = '';
    } catch (error) {
      console.error(`Unable to update route settings for ${path}.`, error);
    } finally {
      saving = false;
      emit('redraw');
    }
  }

  async function updateScreenshotVisibility(path, isPublic, onUpdate) {
    if (saving) return;

    saving = true;
    emit('redraw');
    try {
      const settings = await onUpdate(path, isPublic);
      cacheInfo = { ...cacheInfo, ...settings };
    } catch (error) {
      console.error(`Unable to update screenshot visibility for ${path}.`, error);
    } finally {
      saving = false;
      emit('redraw');
    }
  }

  async function updateRenderer(path, renderer, onUpdate) {
    if (saving || !onUpdate) return;
    saving = true;
    rendererError = null;
    emit('redraw');
    try {
      const settings = await onUpdate(path, renderer || null);
      if (loadedPath === path) cacheInfo = { ...cacheInfo, ...settings };
    } catch (error) {
      rendererError = error.message;
    } finally {
      saving = false;
      emit('redraw');
    }
  }

  return {
    onbeforeupdate: ({ attrs: { path, onLoad } }) => {
      if (path !== loadedPath && !loading) load(path, onLoad);
    },
    view: ({ attrs }) => {
      const {
        site,
        path,
        routes,
        detailsHref,
        backHref,
        overviewHref,
        trafficHref,
        routesHref,
        costHref,
        settingsHref,
        sitesHref,
        onLoad,
        onUpdate,
        onUpdateScreenshotVisibility,
        onUpdateRenderer,
        rendererMetrics,
        usagePeriodHours
      } = attrs;
      const unignorable = cacheInfo?.unignorableQueryParameters || [];
      const alwaysIncluded = cacheInfo?.alwaysIncludedQueryParameters || unignorable;
      const alwaysOmitted = cacheInfo?.alwaysOmittedQueryParameters || [];
      const ignored = (cacheInfo?.ignoredQueryParameters || [])
        .filter(name => !alwaysIncluded.includes(name) && !alwaysOmitted.includes(name));

      if (path !== loadedPath && !loading) load(path, onLoad);

      return m('section.panel.site-detail', [
        m(SiteNavigation, {
          breadcrumbs: [
            { label: sitesHref.startsWith('/admin') ? 'Admin' : 'Sites', href: sitesHref },
            { label: site.name, href: overviewHref },
            { label: 'Routes', href: routesHref },
            { label: path, href: `${detailsHref}?route=${encodeURIComponent(path)}` },
            { label: 'Settings' }
          ],
          backHref,
          backLabel: '← Route details',
          overviewHref,
          trafficHref,
          routesHref,
          costHref,
          settingsHref,
          current: 'routes'
        }),
        m('h2', site.name),
        m(RouteSearch, { routes, detailsHref }),
        m('h3', 'Route settings'),
        m('p.muted', 'Configure rendering, screenshots, and query parameters for this route.'),
        loading ? m('p.muted', 'Loading route settings…') : loadError
          ? m('p', { role: 'alert' }, 'Unable to load route settings. Refresh the page to try again.')
          : m('div.route-settings__content', [
          m('section.route-settings__group', [
            m('h4', 'Rendering'),
            m(RendererSelect, {
              value: cacheInfo?.routeRenderer,
              inheritedRenderer: site.renderOptions?.renderer || 'chromium',
              disabled: saving,
              onchange: event => updateRenderer(path, event.target.value, onUpdateRenderer)
            }),
            m('p.muted', 'The override applies to this pathname and all of its query variants. Choose “Use site setting” to inherit future changes to the site renderer.'),
            saving && m('p.muted', 'Saving route settings…'),
            rendererError && m('p', { role: 'alert' }, rendererError),
            cacheInfo?.renderer && m('p.muted', `Last render: ${cacheInfo.renderer === 'jsdom' ? 'jsdom' : 'Chromium'}.`),
            cacheInfo?.jsdomFallbackReasons?.length > 0 &&
              m('p.muted', `jsdom fallback: ${cacheInfo.jsdomFallbackReasons.join(', ')}`)
          ]),
          m(RendererHealth, {
            comparison: cacheInfo?.lastRendererComparison,
            metrics: rendererMetrics,
            enabled: cacheInfo?.effectiveRenderer === 'jsdom-first',
            periodHours: usagePeriodHours
          }),
          m('section.route-settings__group', [
            m('h4', 'Route screenshot'),
            m('p.muted', 'Public screenshots can be embedded or viewed by anyone with the URL. The page itself and its cached HTML are not made public by this setting.'),
            m(Checkbox, {
              checked: cacheInfo?.publicScreenshot === true,
              disabled: saving,
              label: 'Make this route’s screenshot public',
              onchange: event => updateScreenshotVisibility(path, event.target.checked, onUpdateScreenshotVisibility)
            }),
            cacheInfo?.publicScreenshotUrl && m('div.route-settings__public-url', [
              m('code', cacheInfo.publicScreenshotUrl),
              m('a', {
                href: cacheInfo.publicScreenshotUrl,
                target: '_blank',
                rel: 'noopener'
              }, 'Open image')
            ])
          ]),
          m('section.route-settings__group', [
            m('h4', 'Learned ignored parameters'),
            ignored.length ? m('ul.route-settings__parameters', ignored.map(name =>
              m('li', { key: name }, [
                m('code', name),
                m('button.button.button--quiet', {
                  type: 'button',
                  disabled: saving,
                  onclick: () => updateParameter(path, name, 'include', onUpdate)
                }, 'Always include')
              ])
            )) : m('p.muted', 'No ignored parameters learned for this route.')
          ]),
          m('section.route-settings__group', [
            m('h4', 'Query parameter rules'),
            m('p.muted', 'These rules change cache lookup; the original query string is still sent to your site.'),
            m('div.route-settings__parameter-rules', [
              m('div.route-settings__parameter-rule', [
                m('h5', 'Always include in cache key'),
                alwaysIncluded.length ? m('ul.route-settings__parameters', alwaysIncluded.map(name =>
                  m('li', { key: name }, [
                    m('code', name),
                    m('button.button.button--quiet', {
                      type: 'button',
                      disabled: saving,
                      onclick: () => updateParameter(path, name, 'automatic', onUpdate)
                    }, 'Use automatic')
                  ])
                )) : m('p.muted', 'None configured.'),
                m('form.route-settings__form', {
                  onsubmit: event => {
                    event.preventDefault();
                    updateParameter(path, includeParameter, 'include', onUpdate);
                  }
                }, [
                  m('label', 'Add parameter to always include'),
                  m('div.route-settings__form-row', [
                    m('input', {
                      type: 'text',
                      value: includeParameter,
                      placeholder: 'For example: version',
                      oninput: event => {
                        includeParameter = event.target.value;
                        emit('redraw');
                      }
                    }),
                    m('button.button', {
                      type: 'submit',
                      disabled: saving || !includeParameter.trim()
                    }, saving ? 'Saving…' : 'Add')
                  ])
                ])
              ]),
              m('div.route-settings__parameter-rule', [
                m('h5', 'Always ignore in cache key'),
                alwaysOmitted.length ? m('ul.route-settings__parameters', alwaysOmitted.map(name =>
                  m('li', { key: name }, [
                    m('code', name),
                    m('button.button.button--quiet', {
                      type: 'button',
                      disabled: saving,
                      onclick: () => updateParameter(path, name, 'automatic', onUpdate)
                    }, 'Use automatic')
                  ])
                )) : m('p.muted', 'None configured.'),
                m('form.route-settings__form', {
                  onsubmit: event => {
                    event.preventDefault();
                    updateParameter(path, ignoreParameter, 'omit', onUpdate);
                  }
                }, [
                  m('label', 'Add parameter to always ignore'),
                  m('div.route-settings__form-row', [
                    m('input', {
                      type: 'text',
                      value: ignoreParameter,
                      placeholder: 'For example: domain',
                      oninput: event => {
                        ignoreParameter = event.target.value;
                        emit('redraw');
                      }
                    }),
                    m('button.button.button--quiet', {
                      type: 'submit',
                      disabled: saving || !ignoreParameter.trim()
                    }, saving ? 'Saving…' : 'Add')
                  ])
                ])
              ])
            ])
          ])
        ])
      ]);
    }
  };
});

module.exports = { RouteSettingsPage };
